import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getYouTubeVideoId } from '@/utils/youtube';
import { Plus } from 'lucide-react';

interface AddPresentationVideoDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const categories = ['features', 'tutorials', 'testimonials', 'news'] as const;

export const AddPresentationVideoDialog: React.FC<AddPresentationVideoDialogProps> = ({
  isOpen,
  onOpenChange,
  onSuccess
}) => {
  const { t } = useTranslation('presentation');
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const title = formData.get('title') as string;
    const description = formData.get('description') as string;
    const youtubeUrl = formData.get('youtubeUrl') as string;
    const category = formData.get('category') as string;
    const displayOrder = parseInt(formData.get('displayOrder') as string) || 0;

    const videoId = getYouTubeVideoId(youtubeUrl);
    if (!videoId) {
      toast({
        title: t('addVideoDialog.messages.invalidUrl'),
        variant: 'destructive'
      });
      setLoading(false);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('presentation_videos')
        .insert({
          title,
          description,
          youtube_url: youtubeUrl,
          category,
          display_order: displayOrder,
          created_by: user?.id
        });

      if (error) throw error;

      toast({
        title: t('addVideoDialog.messages.success')
      });
      
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error adding video:', error);
      toast({
        title: t('addVideoDialog.messages.error'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('addVideoDialog.title')}</DialogTitle>
          <DialogDescription>{t('addVideoDialog.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">{t('addVideoDialog.fields.title')}</Label>
            <Input
              id="title"
              name="title"
              placeholder={t('addVideoDialog.fields.titlePlaceholder')}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('addVideoDialog.fields.description')}</Label>
            <Textarea
              id="description"
              name="description"
              placeholder={t('addVideoDialog.fields.descriptionPlaceholder')}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="youtubeUrl">{t('addVideoDialog.fields.youtubeUrl')}</Label>
            <Input
              id="youtubeUrl"
              name="youtubeUrl"
              placeholder={t('addVideoDialog.fields.youtubeUrlPlaceholder')}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">{t('addVideoDialog.fields.category')}</Label>
            <Select name="category" defaultValue="features">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {t(`categories.${cat}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayOrder">{t('addVideoDialog.fields.displayOrder')}</Label>
            <Input
              id="displayOrder"
              name="displayOrder"
              type="number"
              defaultValue={0}
              min={0}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('addVideoDialog.buttons.cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              <Plus className="mr-2 h-4 w-4" />
              {t('addVideoDialog.buttons.add')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
